import React from 'react';
import loadable from '@loadable/component';

import { SevaSethuHero, SevaGoshtiSubscription, LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FallbackPage.module.css';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = error => ({
  sections: [
    {
      sectionType: 'sevaSethuHero',
      sectionId: 'seva-sethu-hero',
    },
    {
      sectionType: 'sevaGoshtiSubscription',
      sectionId: 'seva-goshti-subscription',
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Seva Sethu - ISKCON Marketplace',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Seva Sethu - A devotee marketplace for buying and selling spiritual items, prasadam, and services.',
    },
  },
});

// Seva Sethu Hero Section wrapper for PageBuilder
const SectionSevaSethuHero = props => {
  const { sectionId } = props;
  return (
    <section id={sectionId}>
      <SevaSethuHero />
    </section>
  );
};

// Seva Goshti Subscription Section wrapper for PageBuilder
const SectionSevaGoshtiSubscription = props => {
  const { sectionId } = props;
  return (
    <section id={sectionId}>
      <SevaGoshtiSubscription />
    </section>
  );
};

// Note: this microcopy/translation does not come from translation file.
//       It needs to be something that is not part of fetched assets but built-in text
const SectionMaintenanceMode = props => {
  const { sectionId, error } = props;
  // 404 means that the landing-page asset was not found from the expected asset path
  // which is defined in config.js
  const is404 = error?.status === 404;

  return (
    <section id={sectionId} className={css.root}>
      {is404 ? (
        <div className={css.content}>
          <h2>Oops, something went wrong!</h2>
          <p>
            The marketplace is not fully operational at the moment.
            <br />
            Try refreshing the page and if that does not solve the issue, contact the marketplace
            admins.
          </p>
        </div>
      ) : (
        <div className={css.content}>
          <h2>Oops, something went wrong!</h2>
          <p>{error?.message}</p>
        </div>
      )}
    </section>
  );
};

// This is the fallback page, in case there's no Landing Page asset defined in Console.
const FallbackPage = props => {
  const { error, ...rest } = props;

  // If there's an actual error (not just missing asset), show maintenance mode
  if (error && error.status !== 404) {
    return (
      <PageBuilder
        pageAssetsData={{
          sections: [
            {
              sectionType: 'customMaintenance',
              sectionId: 'maintenance-mode',
              error,
            },
          ],
          meta: {
            pageTitle: { fieldType: 'metaTitle', content: 'Maintenance' },
            pageDescription: { fieldType: 'metaDescription', content: 'Site under maintenance' },
          },
        }}
        options={{
          sectionComponents: {
            customMaintenance: { component: SectionMaintenanceMode },
          },
        }}
        {...rest}
      />
    );
  }

  // Show Seva Sethu landing page with hero and subscription
  return (
    <PageBuilder
      pageAssetsData={fallbackSections(error)}
      options={{
        sectionComponents: {
          sevaSethuHero: { component: SectionSevaSethuHero },
          sevaGoshtiSubscription: { component: SectionSevaGoshtiSubscription },
          customMaintenance: { component: SectionMaintenanceMode },
        },
      }}
      {...rest}
    />
  );
};

export default FallbackPage;
